import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCollab } from '../../contexts/CollabContext';
import './WelcomeScreenUserMenu.css';

interface WelcomeScreenUserMenuProps {
  onLoginClick: () => void;
  onRegisterClick: () => void;
}

export const WelcomeScreenUserMenu: React.FC<WelcomeScreenUserMenuProps> = ({
  onLoginClick,
  onRegisterClick,
}) => {
  const { user, logout } = useAuth();
  const collab = useCollab();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = async () => {
    // Leave session if in one
    if (collab.sessionId) {
      await collab.leaveSession();
    }
    logout();
    setIsMenuOpen(false);
  };

  const handleLoginClick = () => {
    setIsMenuOpen(false);
    onLoginClick();
  };

  const handleRegisterClick = () => {
    setIsMenuOpen(false);
    onRegisterClick();
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromId = (id: string): string => {
    const colors = [
      '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3',
      '#00bcd4', '#009688', '#4caf50', '#ff9800', '#ff5722'
    ];
    // Convert string id to number for color selection
    const numId = parseInt(id, 10) || 0;
    return colors[numId % colors.length];
  };

  return (
    <div className="welcome-user-menu-container" ref={menuRef}>
      <button
        className="welcome-user-menu-button"
        onClick={toggleMenu}
        title={user ? user.name : 'Account'}
      >
        {user ? (
          <div
            className="welcome-user-initials"
            style={{ backgroundColor: getColorFromId(user.id) }}
          >
            {getInitials(user.name)}
          </div>
        ) : (
          <div className="welcome-user-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 1c-2.7 0-8 1.35-8 4v1h16v-1c0-2.65-5.3-4-8-4z" />
            </svg>
          </div>
        )}
      </button>

      {isMenuOpen && (
        <div className="welcome-user-menu">
          {user ? (
            <>
              <div className="welcome-user-menu-header">
                <div
                  className="welcome-user-menu-avatar"
                  style={{ backgroundColor: getColorFromId(user.id) }}
                >
                  {getInitials(user.name)}
                </div>
                <div className="welcome-user-menu-info">
                  <div className="welcome-user-menu-username">{user.name}</div>
                  <div className="welcome-user-menu-email">{user.email}</div>
                </div>
              </div>
              <div className="welcome-user-menu-divider"></div>
              <button className="welcome-user-menu-item" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M11 8a.5.5 0 0 1-.5.5H6.707l1.147 1.146a.5.5 0 0 1-.708.708l-2-2a.5.5 0 0 1 0-.708l2-2a.5.5 0 0 1 .708.708L6.707 7.5H10.5A.5.5 0 0 1 11 8z"/>
                  <path d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
                </svg>
                Logout
              </button>
            </>
          ) : (
            <>
              <button className="welcome-user-menu-item" onClick={handleLoginClick}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M11 8a.5.5 0 0 1-.5.5H6.707l1.147 1.146a.5.5 0 0 1-.708.708l-2-2a.5.5 0 0 1 0-.708l2-2a.5.5 0 0 1 .708.708L6.707 7.5H10.5A.5.5 0 0 1 11 8z"/>
                  <path d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
                </svg>
                Login
              </button>
              <button className="welcome-user-menu-item" onClick={handleRegisterClick}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                  <path fillRule="evenodd" d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5z"/>
                </svg>
                Register
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
